import { defineStore } from 'pinia';
import Api from '../api/Api';
import { useAuthStore } from './AuthStore.js';

const DIALOG_DISMISSED_KEY = 'tw__announcement_dialog_dismissed';
const CACHE_DURATION_MS = 8 * 60 * 60 * 1000; // 8 hours

export const useAnnouncementStore = defineStore('announcement', {
  state: () => ({
    announcements: [],
    isLoading: false,
    error: null,
    shouldAutoShowDialog: false,
    lastFetchedAt: null,
  }),
  getters: {
    getUndismissedAnnouncements: function (state) {
      return state.announcements.filter(a => !a.dismissed);
    },
    getUndismissedCount: function (state) {
      return state.announcements.filter(a => !a.dismissed).length;
    },
    hasUndismissedAnnouncements: function (state) {
      return state.announcements.filter(a => !a.dismissed).length > 0;
    },
    // Visible announcements: undismissed OR persistent (even if dismissed)
    getVisibleAnnouncements: function (state) {
      return state.announcements.filter(a => !a.dismissed || a.persistent);
    },
    getVisibleCount: function (state) {
      return state.announcements.filter(a => !a.dismissed || a.persistent).length;
    },
    hasVisibleAnnouncements: function (state) {
      return state.announcements.some(a => !a.dismissed || a.persistent);
    },
    isCacheValid: function (state) {
      if (!state.lastFetchedAt) return false;
      return (Date.now() - state.lastFetchedAt) < CACHE_DURATION_MS;
    },
  },
  actions: {
    fetchActiveAnnouncements: async function () {
      const authStore = useAuthStore();
      const nodeId = authStore.rootNodeId;

      if (!nodeId) {
        console.error('Cannot fetch announcements: no rootNodeId available');
        return;
      }

      this.isLoading = true;
      this.error = null;

      try {
        const result = await Api.announcement.getActiveAnnouncements(nodeId);
        this.announcements = result.data || [];
        this.lastFetchedAt = Date.now();

        // Clear dismissed state if no announcements returned
        if (this.announcements.length === 0) {
          this.clearDialogDismissedState();
        }
      } catch (err) {
        this.error = err;
        console.error('Failed to fetch announcements:', err);
      } finally {
        this.isLoading = false;
      }
    },
    fetchAndCheckAutoShow: async function () {
      await this.fetchActiveAnnouncements();

      // Set shouldAutoShowDialog based on results and localStorage
      const hasVisible = this.hasVisibleAnnouncements;
      const wasDialogDismissed = this.isDialogDismissed();

      this.shouldAutoShowDialog = hasVisible && !wasDialogDismissed;
    },
    consumeAutoShow: function () {
      this.shouldAutoShowDialog = false;
    },
    isDialogDismissed: function () {
      return window.localStorage.getItem(DIALOG_DISMISSED_KEY) === 'true';
    },
    setDialogDismissed: function () {
      window.localStorage.setItem(DIALOG_DISMISSED_KEY, 'true');
    },
    clearDialogDismissedState: function () {
      window.localStorage.removeItem(DIALOG_DISMISSED_KEY);
    },
    fetchForPageLoad: async function () {
      // Skip fetch if cache is still valid and we have data
      if (this.isCacheValid && this.announcements.length > 0) {
        return;
      }
      await this.fetchActiveAnnouncements();
      // Only fetch announcements for badge count display
      // Do NOT set shouldAutoShowDialog - that should only happen on login via fetchAndCheckAutoShow()
    },
    dismissAnnouncement: async function (id) {
      const authStore = useAuthStore();
      const nodeId = authStore.rootNodeId;

      if (!nodeId) {
        throw new Error('Cannot dismiss announcement: no rootNodeId available');
      }

      // Prevent dismissing persistent announcements
      const announcement = this.announcements.find(a => a.id === id);
      if (announcement && announcement.persistent) {
        throw new Error('Persistent announcements cannot be dismissed');
      }

      try {
        await Api.announcement.dismissAnnouncement(nodeId, id);
        if (announcement) {
          announcement.dismissed = true;
        }

        // Clear dismissed state if no more visible announcements
        if (!this.hasVisibleAnnouncements) {
          this.clearDialogDismissedState();
        }
      } catch (err) {
        this.error = err;
        console.error('Failed to dismiss announcement:', err);
        throw err;
      }
    },
    dismissAllAnnouncements: async function () {
      const authStore = useAuthStore();
      const nodeId = authStore.rootNodeId;

      if (!nodeId) {
        throw new Error('Cannot dismiss announcements: no rootNodeId available');
      }

      // Only dismiss non-persistent announcements
      const dismissable = this.announcements.filter(a => !a.dismissed && !a.persistent);
      const errors = [];

      for (const announcement of dismissable) {
        try {
          await Api.announcement.dismissAnnouncement(nodeId, announcement.id);
          announcement.dismissed = true;
        } catch (err) {
          errors.push(err);
          console.error(`Failed to dismiss announcement ${announcement.id}:`, err);
        }
      }

      // Clear dismissed state if no more visible announcements
      if (!this.hasVisibleAnnouncements) {
        this.clearDialogDismissedState();
      }

      if (errors.length > 0) {
        this.error = errors[0];
        throw errors[0];
      }
    },
  },
});
